import { ApiError } from '../types/api';

export interface HttpClientConfig {
  baseURL: string;
  timeout?: number;
  defaultHeaders?: Record<string, string>;
}

export interface RequestConfig {
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
}

export class HttpClient {
  private baseURL: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;

  constructor(config: HttpClientConfig) {
    this.baseURL = config.baseURL.replace(/\/$/, ''); // Remove trailing slash
    this.timeout = config.timeout || 10000; // 10 seconds default
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.defaultHeaders,
    };
  }

  /**
   * In-flight refresh, shared across callers.
   *
   * Without this, a page firing several requests at once would each get a 401
   * and each start its own refresh — a stampede that rotates the refresh token
   * repeatedly and invalidates the others.
   */
  private refreshInFlight: Promise<boolean> | null = null;

  /** Refresh endpoint must never be retried through the 401 path. */
  private static readonly REFRESH_PATH = '/auth/refresh';

  /**
   * Exchange the stored refresh token for a fresh access token.
   *
   * Returns:
   *   Promise<boolean>: True when a new access token was installed.
   */
  /**
   * Forget every stored credential on this device.
   *
   * SECURITY. Logging out cleared the auth store and the in-memory header but
   * left the REFRESH TOKEN in localStorage. The next 401 therefore refreshed
   * silently, minted a new access token for the previous user, and rewrote the
   * auth store to keep them signed in -- so signing out and registering a new
   * account landed back inside the previous account's data.
   *
   * Everything that can re-authenticate has to go, in one place, or the next
   * caller forgets one of them again.
   */
  public clearStoredCredentials(): void {
    try {
      localStorage.removeItem('folioworth_tokens');
      localStorage.removeItem('folioworth-auth');
    } catch {
      // Private browsing can throw on localStorage; the in-memory token below
      // is cleared regardless, so a failure here must not abort the logout.
    }
    this.refreshInFlight = null;
    this.setAuthToken(null);
  }

  private async refreshSession(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      try {
        const raw = localStorage.getItem('folioworth_tokens');
        if (!raw) return false;
        const stored = JSON.parse(raw);
        if (!stored?.refresh_token) return false;

        const res = await fetch(`${this.baseURL}${HttpClient.REFRESH_PATH}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: stored.refresh_token }),
        });
        if (!res.ok) return false;

        const body = await res.json();
        if (!body?.access_token) return false;

        localStorage.setItem('folioworth_tokens', JSON.stringify({
          access_token: body.access_token,
          refresh_token: body.refresh_token ?? stored.refresh_token,
        }));
        this.setAuthToken(body.access_token);

        // Keep the persisted auth store in step so a reload stays signed in.
        try {
          const authRaw = localStorage.getItem('folioworth-auth');
          if (authRaw) {
            const parsed = JSON.parse(authRaw);
            if (parsed?.state) {
              parsed.state.token = body.access_token;
              localStorage.setItem('folioworth-auth', JSON.stringify(parsed));
            }
          }
        } catch { /* non-fatal: the token itself is already installed */ }

        return true;
      } catch {
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  private async request<T>(
    method: string,
    url: string,
    data?: any,
    config?: RequestConfig,
    isRetry = false,
  ): Promise<T> {
    const fullURL = `${this.baseURL}${url}`;
    const headers: Record<string, string> = { ...this.defaultHeaders, ...config?.headers };

    // A multipart body must NOT be stringified, and must NOT carry an explicit
    // Content-Type: the browser sets it along with the boundary, and any value
    // we supply overwrites that and makes the body unparseable server-side.
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    if (isFormData) {
      delete headers['Content-Type'];
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config?.timeout || this.timeout);
    
    // Use provided signal or our timeout signal
    const signal = config?.signal || controller.signal;

    try {
      const response = await fetch(fullURL, {
        method,
        headers,
        body: isFormData ? (data as FormData) : data ? JSON.stringify(data) : undefined,
        signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 401 && !isRetry && url !== HttpClient.REFRESH_PATH) {
        // Try once to refresh, then replay the original request. Only a failed
        // refresh means the session is genuinely over.
        if (await this.refreshSession()) {
          return this.request<T>(method, url, data, config, true);
        }
      }

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      // Handle empty responses (like DELETE operations)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return {} as T;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }
        // If it's already an ApiError or has a message, pass it through
        if (error.message && error.message !== 'Unknown error occurred') {
          throw error;
        }
        // Otherwise, create a more informative error
        throw new Error(`Request failed: ${error.message || 'Unknown error occurred'}`);
      }
      
      throw new Error('Unknown error occurred');
    }
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    let error: ApiError;
    
    try {
      const errorData = await response.json();
      const errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      error = {
        message: errorMessage,
        code: errorData.code || response.status.toString(),
        details: errorData.details || errorData,
      };
    } catch (parseErr) {
      // If we can't parse the error response, create a generic error with status info
      const statusText = response.statusText || 'Unknown error';
      error = {
        message: `HTTP ${response.status}: ${statusText}`,
        code: response.status.toString(),
      };
    }

    // Create an Error object with the error message so it can be caught properly
    const errorObj = new Error(error.message);
    (errorObj as any).code = error.code;
    (errorObj as any).details = error.details;
    throw errorObj;
  }

  public async get<T>(url: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('GET', url, undefined, config);
  }

  public async post<T>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>('POST', url, data, config);
  }

  /**
   * POST a multipart form, for file uploads.
   *
   * Separate from post() only so call sites read clearly; request() detects
   * FormData itself, so passing one to post() also works.
   */
  public async postForm<T>(url: string, form: FormData, config?: RequestConfig): Promise<T> {
    return this.request<T>('POST', url, form, config);
  }

  public async put<T>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>('PUT', url, data, config);
  }

  public async patch<T>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>('PATCH', url, data, config);
  }

  public async delete<T>(url: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('DELETE', url, undefined, config);
  }

  public setAuthToken(token: string | null): void {
    if (token) {
      this.defaultHeaders['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.defaultHeaders['Authorization'];
    }
  }

  public setDefaultHeader(key: string, value: string): void {
    this.defaultHeaders[key] = value;
  }

  public removeDefaultHeader(key: string): void {
    delete this.defaultHeaders[key];
  }
}

// Create a default HTTP client instance
// Use relative URL by default so it goes through Vite proxy (localhost:5173 -> localhost:8080)
// This allows the frontend to work in Docker (proxied) or locally
// For local development, Vite proxy forwards /api/* to http://localhost:8080
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const httpClient = new HttpClient({
  baseURL: API_BASE_URL,
  timeout: 10000,
});
